import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../reduxStore';
import { hideLoader, showLoader } from './loaderSlice';
import { showErrorSnackbar, showSuccessSnackbar } from './snackbarSlice';
import {
  createStoreNoteApi,
  getOrderHistory,
  getOrderHistoryByPage
} from '../../ApiServices/OrderHistoryService';
import { t } from 'i18next';
import { Analytics } from '@mui/icons-material';

export const initialState = {
  value: { totalCount: -1, data: [{}] },
  loading: false,
  error: null,
  selectedValue: null,
  rowsPerPage: 10,
  pageNo: 0,
  filter: {}
};

const OrderHistorySlice = createSlice({
  name: 'orderHistory',
  initialState: initialState,
  reducers: {
    clearOrderHistoryState(state: any) {
      state.rowsPerPage = initialState.rowsPerPage;
      state.pageNo = initialState.pageNo;
      state.value = initialState.value;
      state.loading = false;
      state.error = null;
      state.selectedValue = initialState.selectedValue;
      state.message = null;
      state.filter = initialState.filter;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchOrderHistory.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.value = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      // state.rowsPerPage = action?.meta?.arg?.limit;
      // state.sortBy = action?.meta?.arg?.sortBy;
      // state.sortDirection = action?.meta?.arg?.sortDirection;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchOrderHistory.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchOrderHistoryById.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = action?.payload?.data;
    });
    builder.addCase(fetchOrderHistoryById.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.error = action?.payload;
    });
  }
});

export const OrderHistoryState = (state: RootState) => state?.OrderHistory;
export const { clearOrderHistoryState } = OrderHistorySlice.actions;
export default OrderHistorySlice.reducer;

export const fetchOrderHistory = createAsyncThunk(
  'orderHistory/fetchOrderHistory',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_PRODUCTS') }));
      return fulfillWithValue(await getOrderHistory(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchOrderHistoryById = createAsyncThunk(
  'orderHistory/fetchOrderHistoryById',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_ORDERHISTORY_DETAILS') }));
      return fulfillWithValue(await getOrderHistoryByPage(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadOrders = createAsyncThunk(
  'orderHistory/downloadOrders',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_ORDERS') }));
      let response: any = await getOrderHistory(payload);
      if (response === 200) dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const createStoreNote = createAsyncThunk(
  'orders/createStoreNote',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('ADDING_NEW_NOTE') }));
      let response = await createStoreNoteApi(payload);
      dispatch(showSuccessSnackbar({ message: t('NOTE_CREATED_SUCCESSFULLY') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadOrderDetails = createAsyncThunk(
  'orderHistory/downloadOrderDetails',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_ORDER_HISTORY_DETAILS') }));
      let response: any = await getOrderHistoryByPage(payload);
      if (response === 200) dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);